import { useMemo, useState } from "react";
import { ActionPlanService } from "../../api/ActionPlanService";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import { TrainingModuleOption } from "../../types/actionPlan";
import { toScheduledTrainingModules } from "../../utils/ActionPlan.utils";
import { Button } from "../ui/Button/Button";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import { Typography } from "../ui/Typography/Typography";
import { ModuleScheduleStep } from "./ModuleScheduleStep";
import { SuggestedActivitiesStep } from "./SuggestedActivitiesStep";
import { TrainingModulePicker } from "./TrainingModulePicker";
import { WizardStepLayout } from "./WizardStepLayout";
import "./ActionPlanTrainingWizard.scss";

interface WizardStepConfig {
  title: string;
  description: string;
  nextLabel: string;
  backLabel: string;
  tooltipText?: string;
}

interface ActionPlanTrainingWizardProps {
  trainingModuleOptions?: TrainingModuleOption[];
  onClose?: () => void;
  onActivitiesAdd: (activities: Activity[]) => Promise<void>;
  onActivitiesRefetch?: () => void;
}
export const ActionPlanTrainingWizard = ({
  trainingModuleOptions,
  onClose,
  onActivitiesAdd,
  onActivitiesRefetch,
}: ActionPlanTrainingWizardProps) => {
  const { t } = useTranslation("actionPlanPage");
  const [selectedModules, setSelectedModules] = useState<
    TrainingModuleOption[]
  >([]);
  const [suggestedActivities, setSuggestedActivities] = useState<Activity[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [step, setStep] = useState(1);
  const actionPlanService = new ActionPlanService();

  const wizardSteps: Record<number, WizardStepConfig> = {
    1: {
      title: t("addModules.selectModule.title"),
      description: t("addModules.selectModule.description"),
      nextLabel: t("addModules.actions.next"),
      backLabel: t("addModules.actions.cancel"),
      tooltipText: t("addModules.selectModule.tooltip"),
    },
    2: {
      title: t("addModules.scheduleModule.title"),
      description: t("addModules.scheduleModule.description"),
      nextLabel: t("addModules.actions.next"),
      backLabel: t("addModules.actions.back"),
      tooltipText: t("addModules.scheduleModule.tooltip"),
    },
    3: {
      title: t("addModules.overview.title"),
      description: t("addModules.overview.description"),
      nextLabel: t("addModules.actions.confirm"),
      backLabel: t("addModules.actions.back"),
    },
  };

  const currentStep = wizardSteps[step];

  const isNextDisabled = useMemo(() => {
    if (step === 1) {
      return selectedModules.length === 0;
    }
    if (step === 2) {
      return selectedModules.some((m) => !m.scheduledDate);
    }
    return false;
  }, [step, selectedModules]);

  const handleSelectionChange = (
    module: TrainingModuleOption,
    selected: boolean
  ) => {
    if (selected) {
      setSelectedModules((prev) => [...prev, module]);
    } else {
      setSelectedModules((prev) =>
        prev.filter(
          (m) =>
            m.trainingModule.referenceId !== module.trainingModule.referenceId
        )
      );
    }
  };

  const handleDateChange = (moduleId: string, date: Date) => {
    setSelectedModules((prev) =>
      prev.map((m) =>
        m.trainingModule.referenceId === moduleId
          ? { ...m, scheduledDate: date }
          : m
      )
    );
  };

  const handleGetSuggestions = async () => {
    setIsLoading(true);
    try {
      const scheduledForApi = toScheduledTrainingModules(selectedModules);
      const response =
        await actionPlanService.getActivityTrainingModuleSuggestions(
          scheduledForApi
        );
      const activities = response.map((activity) => new Activity(activity));
      setSuggestedActivities(activities);
      setStep((prev) => prev + 1);
    } catch (error) {
      setError(t("addModules.errors.fetchSuggestions") || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    setError("");
    if (step === 2) {
      await handleGetSuggestions();
    } else if (step === 3) {
      await handleActivitiesAdd();
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setError("");
    if (step === 1) {
      onClose && onClose();
    } else {
      setStep((prev) => prev - 1);
    }
  };

  const handleActivitiesAdd = async () => {
    setIsLoading(true);
    try {
      await onActivitiesAdd(suggestedActivities);
      onActivitiesRefetch?.();
      onClose?.();
    } catch {
      setError(t("addModules.errors.saveSuggestions") || "");
    } finally {
      setIsLoading(false);
    }
  };

  const renderSteps = () => {
    switch (step) {
      case 1:
        return (
          <WizardStepLayout
            title={currentStep.title}
            description={currentStep.description}
          >
            <TrainingModulePicker
              trainingModuleOptions={trainingModuleOptions}
              selectedModules={selectedModules}
              onSelectModule={handleSelectionChange}
            />
          </WizardStepLayout>
        );
      case 2:
        return (
          <WizardStepLayout
            title={currentStep.title}
            description={currentStep.description}
          >
            <ModuleScheduleStep
              selectedModules={selectedModules}
              onDateChange={handleDateChange}
            />
          </WizardStepLayout>
        );
      case 3:
        return (
          <WizardStepLayout
            title={currentStep.title}
            description={currentStep.description}
          >
            <SuggestedActivitiesStep
              activities={suggestedActivities}
              setActivities={setSuggestedActivities}
            />
          </WizardStepLayout>
        );
    }
  };

  const renderButtons = () => {
    return (
      <div className="ActionPlanTrainingWizard__buttons">
        {currentStep.backLabel && (
          <Button
            color="secondary"
            variant="outlined"
            onClick={handleBack}
            disabled={isLoading}
            data-testid="wizard-back-button"
          >
            {currentStep.backLabel}
          </Button>
        )}
        <Tooltip
          placement="top"
          title={(isNextDisabled && currentStep.tooltipText) || ""}
        >
          <span>
            <Button
              variant="contained"
              color="info"
              onClick={handleNext}
              disabled={isLoading || isNextDisabled}
              loading={isLoading}
              data-testid="wizard-next-button"
            >
              {currentStep.nextLabel}
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  };
  return (
    <div className="ActionPlanTrainingWizard">
      {renderSteps()}
      {renderButtons()}
      {error && (
        <Typography
          desktop="body2"
          color="error"
          className="ActionPlanTrainingWizard__error"
        >
          {error}
        </Typography>
      )}
    </div>
  );
};
